/**
 * Section shadowing document references for the Firebase client SDK — typed
 * converters + ref builders for feedback, moderation, assignments, and coverage.
 */
import type {
  CollectionReference,
  DocumentReference,
  Query,
} from 'firebase/firestore';
import {
  collection,
  collectionGroup,
  doc,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';

import {
  SECTION_ANALYSIS_SHADOW_FEEDBACK_SUBCOLLECTION,
  SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION,
} from '../documents/section/sectionAnalysisFirestorePaths';

import type {SectionId, SectionWeekId} from '../../section/sectionApi';
import {
  ShadowRubricSchema,
  type ShadowRubric,
} from '../../section/shadowRubric';
import type {CourseId} from '../../types/branded';
import {
  getShadowFeedbackCollectionPath,
  getShadowFeedbackDocumentPath,
  getShadowFeedbackModerationDocumentPath,
  getShadowFeedbackRepliesCollectionPath,
  getShadowStaffFlagDocumentPath,
  SHADOW_FEEDBACK_REPLIES_SUBCOLLECTION,
  ShadowFeedbackDocumentSchema,
  ShadowFeedbackModerationDocumentSchema,
  ShadowFeedbackReplyDocumentSchema,
  ShadowStaffFlagDocumentSchema,
  type ShadowFeedbackDocument,
  type ShadowFeedbackModerationDocument,
  type ShadowFeedbackReplyDocument,
  type ShadowStaffFlagDocument,
  type ShadowStaffFlagRaisedBy,
} from '../documents/section/shadowFeedback';
import {
  getShadowAssignmentDocumentPath,
  getShadowAssignmentsCollectionPath,
  getShadowConfigDocumentPath,
  getShadowCoverageCollectionPath,
  getShadowCoverageDocumentPath,
  getShadowNominationDocumentPath,
  getShadowNominationsCollectionPath,
  getShadowRubricHistoryCollectionPath,
  getShadowRubricHistoryDocumentPath,
  ShadowAssignmentDocumentSchema,
  ShadowConfigDocumentSchema,
  ShadowCoverageDocumentSchema,
  ShadowNominationDocumentSchema,
  type ShadowAssignmentDocument,
  type ShadowConfigDocument,
  type ShadowCoverageDocument,
  type ShadowNominationDocument,
} from '../documents/section/sectionShadowQueue';
import {
  getShadowStaffFlagTriageCollectionPath,
  getShadowStaffFlagTriageDocumentPath,
  getShadowStaffFlagTriageNotesCollectionPath,
  ShadowStaffFlagTriageDocumentSchema,
  ShadowStaffFlagTriageNoteDocumentSchema,
  type ShadowStaffFlagTriageDocument,
  type ShadowStaffFlagTriageNoteDocument,
} from '../documents/section/shadowStaffFlagTriage';
import {createConverter} from './sdk';

const SHADOW_FEEDBACK_CONVERTER = createConverter<ShadowFeedbackDocument>({
  schema: ShadowFeedbackDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: true},
});

const SHADOW_FEEDBACK_REPLY_CONVERTER =
  createConverter<ShadowFeedbackReplyDocument>({
    // Replies are server-written (postshadowreply); the client only reads them.
    schema: ShadowFeedbackReplyDocumentSchema,
    options: {validateOnRead: true, validateOnWrite: false},
  });

const SHADOW_FEEDBACK_MODERATION_CONVERTER =
  createConverter<ShadowFeedbackModerationDocument>({
    // Moderation verdicts are server-written; the client only reads them.
    schema: ShadowFeedbackModerationDocumentSchema,
    options: {validateOnRead: true, validateOnWrite: false},
  });

const SHADOW_STAFF_FLAG_CONVERTER = createConverter<ShadowStaffFlagDocument>({
  // Written through a raw ref (server timestamps); the client reads it back.
  schema: ShadowStaffFlagDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const SHADOW_STAFF_FLAG_TRIAGE_CONVERTER =
  createConverter<ShadowStaffFlagTriageDocument>({
    // Written with field transforms (server timestamps, increments).
    schema: ShadowStaffFlagTriageDocumentSchema,
    options: {validateOnRead: true, validateOnWrite: false},
  });

const SHADOW_STAFF_FLAG_TRIAGE_NOTE_CONVERTER =
  createConverter<ShadowStaffFlagTriageNoteDocument>({
    schema: ShadowStaffFlagTriageNoteDocumentSchema,
    options: {validateOnRead: true, validateOnWrite: false},
  });

const SHADOW_ASSIGNMENT_CONVERTER = createConverter<ShadowAssignmentDocument>({
  schema: ShadowAssignmentDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const SHADOW_COVERAGE_CONVERTER = createConverter<ShadowCoverageDocument>({
  // Coverage rollups are server-maintained; the client only reads them.
  schema: ShadowCoverageDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const SHADOW_CONFIG_CONVERTER = createConverter<ShadowConfigDocument>({
  // Read by any SL (queue timing + current rubric); written by admins.
  schema: ShadowConfigDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const SHADOW_NOMINATION_CONVERTER = createConverter<ShadowNominationDocument>({
  // Nominations are server-written; the reviewee's opt-out toggle is the only
  // client write and goes through a callable, so this stays read-only here.
  schema: ShadowNominationDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const SHADOW_RUBRIC_CONVERTER = createConverter<ShadowRubric>({
  // Superseded rubric versions; read to render feedback written against them.
  schema: ShadowRubricSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/** Typed collection ref for all peer feedback on a section week. */
export function getShadowFeedbackCollectionRef({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): CollectionReference<ShadowFeedbackDocument> {
  return collection(
    getFirestore(),
    getShadowFeedbackCollectionPath({courseId, sectionId, sectionWeek}),
  ).withConverter(SHADOW_FEEDBACK_CONVERTER);
}

/** Typed doc ref for one reviewer's feedback on a section week. */
export function getShadowFeedbackDocRef({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
}): DocumentReference<ShadowFeedbackDocument> {
  return doc(
    getFirestore(),
    getShadowFeedbackDocumentPath({
      courseId,
      sectionId,
      sectionWeek,
      reviewerUid,
    }),
  ).withConverter(SHADOW_FEEDBACK_CONVERTER);
}

/**
 * Collection-group query of all moderation-approved feedback about a reviewee,
 * across every section and week. Requires a composite collection-group index on
 * (`revieweeUid`, `moderationStatus`).
 */
export function getReceivedShadowFeedbackQuery({
  revieweeUid,
}: {
  revieweeUid: string;
}): Query<ShadowFeedbackDocument> {
  return query(
    collectionGroup(
      getFirestore(),
      SECTION_ANALYSIS_SHADOW_FEEDBACK_SUBCOLLECTION,
    ).withConverter(SHADOW_FEEDBACK_CONVERTER),
    where('revieweeUid', '==', revieweeUid),
    where('moderationStatus', '==', 'approved'),
  );
}

/**
 * Collection-group query of feedback awaiting human moderation in a course, for
 * the staff moderation queue. Requires a composite collection-group index on
 * (`courseId`, `moderationStatus`).
 */
export function getShadowModerationQueueQuery({
  courseId,
}: {
  courseId: string;
}): Query<ShadowFeedbackDocument> {
  return query(
    collectionGroup(
      getFirestore(),
      SECTION_ANALYSIS_SHADOW_FEEDBACK_SUBCOLLECTION,
    ).withConverter(SHADOW_FEEDBACK_CONVERTER),
    where('courseId', '==', courseId),
    where('moderationStatus', '==', 'needs_review'),
  );
}

/**
 * Collection-group query of shadow-feedback replies awaiting human moderation in
 * a course, for the staff reply-moderation queue. Requires a composite
 * collection-group index on (`courseId`, `moderationStatus`).
 */
export function getShadowReplyModerationQueueQuery({
  courseId,
}: {
  courseId: string;
}): Query<ShadowFeedbackReplyDocument> {
  return query(
    collectionGroup(
      getFirestore(),
      SHADOW_FEEDBACK_REPLIES_SUBCOLLECTION,
    ).withConverter(SHADOW_FEEDBACK_REPLY_CONVERTER),
    where('courseId', '==', courseId),
    where('moderationStatus', '==', 'needs_review'),
  );
}

/**
 * Typed doc ref for a flag to course staff on one section-week's review.
 *
 * @param params - The flag's location.
 * @param params.courseId - Course of the reviewed section.
 * @param params.sectionId - The reviewed section.
 * @param params.sectionWeek - 1-based week of the review.
 * @param params.reviewerUid - Reviewer of the section-week.
 * @param params.raisedBy - Who raises the flag. Omitted ⇒ the reviewer's flag.
 * @return The flag's typed document reference.
 * @throws Never.
 */
export function getShadowStaffFlagDocRef({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
  raisedBy,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
  raisedBy?: ShadowStaffFlagRaisedBy;
}): DocumentReference<ShadowStaffFlagDocument> {
  return doc(
    getFirestore(),
    getShadowStaffFlagDocumentPath({
      courseId,
      sectionId,
      sectionWeek,
      reviewerUid,
      raisedBy,
    }),
  ).withConverter(SHADOW_STAFF_FLAG_CONVERTER);
}

/**
 * Collection-group query of every flag to course staff in a course, from
 * reviewers and reviewees alike, for the staff list. Requires a
 * collection-group index on `courseId`.
 */
export function getShadowStaffFlagsQuery({
  courseId,
}: {
  courseId: string;
}): Query<ShadowStaffFlagDocument> {
  return query(
    collectionGroup(
      getFirestore(),
      SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION,
    ).withConverter(SHADOW_STAFF_FLAG_CONVERTER),
    where('courseId', '==', courseId),
  );
}

/**
 * Typed collection ref for staff triage of a course's flags to course staff.
 *
 * @param params.courseId - The course.
 * @return The collection's typed reference.
 * @throws Never.
 */
export function getShadowStaffFlagTriageCollectionRef({
  courseId,
}: {
  courseId: CourseId;
}): CollectionReference<ShadowStaffFlagTriageDocument> {
  return collection(
    getFirestore(),
    getShadowStaffFlagTriageCollectionPath({courseId}),
  ).withConverter(SHADOW_STAFF_FLAG_TRIAGE_CONVERTER);
}

/**
 * Typed doc ref for staff triage of one flag.
 *
 * @param params.courseId - The course.
 * @param params.triageId - From `getShadowStaffFlagTriageId`.
 * @return The doc's typed reference.
 * @throws Never.
 */
export function getShadowStaffFlagTriageDocRef({
  courseId,
  triageId,
}: {
  courseId: CourseId;
  triageId: string;
}): DocumentReference<ShadowStaffFlagTriageDocument> {
  return doc(
    getFirestore(),
    getShadowStaffFlagTriageDocumentPath({courseId, triageId}),
  ).withConverter(SHADOW_STAFF_FLAG_TRIAGE_CONVERTER);
}

/**
 * Typed collection ref for the staff notes on one flag.
 *
 * @param params.courseId - The course.
 * @param params.triageId - From `getShadowStaffFlagTriageId`.
 * @return The collection's typed reference.
 * @throws Never.
 */
export function getShadowStaffFlagTriageNotesCollectionRef({
  courseId,
  triageId,
}: {
  courseId: CourseId;
  triageId: string;
}): CollectionReference<ShadowStaffFlagTriageNoteDocument> {
  return collection(
    getFirestore(),
    getShadowStaffFlagTriageNotesCollectionPath({courseId, triageId}),
  ).withConverter(SHADOW_STAFF_FLAG_TRIAGE_NOTE_CONVERTER);
}

/** Typed doc ref for the server-written moderation verdict on one feedback doc. */
export function getShadowFeedbackModerationDocRef({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
}): DocumentReference<ShadowFeedbackModerationDocument> {
  return doc(
    getFirestore(),
    getShadowFeedbackModerationDocumentPath({
      courseId,
      sectionId,
      sectionWeek,
      reviewerUid,
    }),
  ).withConverter(SHADOW_FEEDBACK_MODERATION_CONVERTER);
}

/**
 * Typed collection ref for a feedback's reply thread. Query it constrained to a
 * rule-safe branch: `where('authorUid','==',uid)` for one's own replies, or
 * `where('<party>Uid','==',uid)` + `where('moderationStatus','==','approved')`
 * for the approved replies the caller (as reviewer or reviewee) may see.
 */
export function getShadowFeedbackRepliesCollectionRef({
  courseId,
  sectionId,
  sectionWeek,
  reviewerUid,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  reviewerUid: string;
}): CollectionReference<ShadowFeedbackReplyDocument> {
  return collection(
    getFirestore(),
    getShadowFeedbackRepliesCollectionPath({
      courseId,
      sectionId,
      sectionWeek,
      reviewerUid,
    }),
  ).withConverter(SHADOW_FEEDBACK_REPLY_CONVERTER);
}

/**
 * Typed collection ref for a course's shadow assignments. Query it with
 * `where('reviewerUid', '==', uid)` for a reviewer's queue, or
 * `where('sectionId', '==', id)` for a section's reviewers.
 */
export function getShadowAssignmentsCollectionRef({
  courseId,
}: {
  courseId: CourseId;
}): CollectionReference<ShadowAssignmentDocument> {
  return collection(
    getFirestore(),
    getShadowAssignmentsCollectionPath({courseId}),
  ).withConverter(SHADOW_ASSIGNMENT_CONVERTER);
}

/** Typed doc ref for a single shadow assignment. */
export function getShadowAssignmentDocRef({
  courseId,
  assignmentId,
}: {
  courseId: CourseId;
  assignmentId: string;
}): DocumentReference<ShadowAssignmentDocument> {
  return doc(
    getFirestore(),
    getShadowAssignmentDocumentPath({courseId, assignmentId}),
  ).withConverter(SHADOW_ASSIGNMENT_CONVERTER);
}

/** Typed collection ref for a course's per-section-week coverage rollups. */
export function getShadowCoverageCollectionRef({
  courseId,
}: {
  courseId: CourseId;
}): CollectionReference<ShadowCoverageDocument> {
  return collection(
    getFirestore(),
    getShadowCoverageCollectionPath({courseId}),
  ).withConverter(SHADOW_COVERAGE_CONVERTER);
}

/** Typed doc ref for a single coverage rollup. */
export function getShadowCoverageDocRef({
  courseId,
  coverageId,
}: {
  courseId: CourseId;
  coverageId: string;
}): DocumentReference<ShadowCoverageDocument> {
  return doc(
    getFirestore(),
    getShadowCoverageDocumentPath({courseId, coverageId}),
  ).withConverter(SHADOW_COVERAGE_CONVERTER);
}

/**
 * Typed collection ref for a course's "Teaching Star" moment nominations. The
 * rules let a client list only the nominations it may read, so list it through
 * {@link getSharedShadowNominationsQuery} or
 * {@link getRevieweeShadowNominationsQuery} rather than directly.
 */
export function getShadowNominationsCollectionRef({
  courseId,
}: {
  courseId: CourseId;
}): CollectionReference<ShadowNominationDocument> {
  return collection(
    getFirestore(),
    getShadowNominationsCollectionPath({courseId}),
  ).withConverter(SHADOW_NOMINATION_CONVERTER);
}

/** Typed doc ref for a single nomination. */
export function getShadowNominationDocRef({
  courseId,
  nominationId,
}: {
  courseId: CourseId;
  nominationId: string;
}): DocumentReference<ShadowNominationDocument> {
  return doc(
    getFirestore(),
    getShadowNominationDocumentPath({courseId, nominationId}),
  ).withConverter(SHADOW_NOMINATION_CONVERTER);
}

/**
 * Query of the moments their reviewees have shared, for the Teaching Stars
 * browse page. The filter is what lets a reviewer below staff list the
 * collection under the `nominations` rule; an unshared moment, whose field is
 * false or absent, is left out.
 */
export function getSharedShadowNominationsQuery({
  courseId,
}: {
  courseId: CourseId;
}): Query<ShadowNominationDocument> {
  return query(
    getShadowNominationsCollectionRef({courseId}),
    where('sharedByReviewee', '==', true),
  );
}

/**
 * Query of a reviewee's own showcased moments, for the opt-in surface: every
 * nomination whose `revieweeUid` is the caller. The reviewee sees their private
 * (un-shared) ones too, so this does not filter on `sharedByReviewee`.
 */
export function getRevieweeShadowNominationsQuery({
  courseId,
  revieweeUid,
}: {
  courseId: CourseId;
  revieweeUid: string;
}): Query<ShadowNominationDocument> {
  return query(
    getShadowNominationsCollectionRef({courseId}),
    where('revieweeUid', '==', revieweeUid),
  );
}

/** Typed doc ref for a course's shadowing config (guaranteed weeks, timing). */
export function getShadowConfigDocRef({
  courseId,
}: {
  courseId: CourseId;
}): DocumentReference<ShadowConfigDocument> {
  return doc(
    getFirestore(),
    getShadowConfigDocumentPath({courseId}),
  ).withConverter(SHADOW_CONFIG_CONVERTER);
}

/** Typed doc ref for one superseded rubric version. */
export function getShadowRubricHistoryDocRef({
  courseId,
  versionId,
}: {
  courseId: CourseId;
  versionId: string;
}): DocumentReference<ShadowRubric> {
  return doc(
    getFirestore(),
    getShadowRubricHistoryDocumentPath({courseId, versionId}),
  ).withConverter(SHADOW_RUBRIC_CONVERTER);
}

/** Typed collection ref for a course's superseded rubric versions. */
export function getShadowRubricHistoryCollectionRef({
  courseId,
}: {
  courseId: CourseId;
}): CollectionReference<ShadowRubric> {
  return collection(
    getFirestore(),
    getShadowRubricHistoryCollectionPath({courseId}),
  ).withConverter(SHADOW_RUBRIC_CONVERTER);
}
